import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import * as authService from "../services/authService";
import { isStrongEnoughPassword } from "../utils/validators";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";

  const [form, setForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const nextErrors = {};

    if (!token) {
      nextErrors.token = "Your reset link is incomplete or missing.";
    }

    if (!isStrongEnoughPassword(form.newPassword)) {
      nextErrors.newPassword = "Your password must be at least 8 characters.";
    }

    if (form.confirmPassword !== form.newPassword) {
      nextErrors.confirmPassword = "The passwords do not match.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      token: "",
    }));

    setServerMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm();
    setErrors(nextErrors);
    setServerMessage("");

    if (Object.keys(nextErrors).length > 0) return;

    try {
      setLoading(true);

      const data = await authService.resetPassword({
        token,
        newPassword: form.newPassword,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Password reset failed.");
      }

      navigate("/action-status", {
        replace: true,
        state: {
          status: "success",
          eyebrow: "Password Updated",
          title: "Your password has been reset successfully",
          message:
            data?.message ||
            "Your password has been updated. You can now sign in with your new password.",
          note:
            "For your security, please use your new password the next time you log in.",
          actions: [
            {
              label: "Go to Sign In",
              to: "/login",
              variant: "primary",
            },
            {
              label: "Back to Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setServerMessage(err?.message || "We could not reset your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Set a New Password"
            title="Create your new password"
            subtitle="Enter your new password below to complete the reset process and return to your account."
            className="mb-8"
          />

          {errors.token ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{errors.token}</p>
            </div>
          ) : null}

          {serverMessage ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{serverMessage}</p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              value={form.newPassword}
              onChange={handleChange}
              error={errors.newPassword}
              required
              showPasswordToggle
            />

            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
              showPasswordToggle
            />

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              Update Password
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-warm">
            Back to{" "}
            <Link to="/login" className="font-semibold text-henna hover:underline">
              sign in
            </Link>
          </p>
        </div>
      </PageContainer>
    </div>
  );
};

export default ResetPassword;